import { useParams, useSearchParams } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { useInvoiceDetail } from '@/api/invoices';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { InvoiceDetailHeader } from './invoice-detail-header';
import { InvoiceTotalsCard } from './invoice-totals-card';
import { InvoiceInfoCard } from './invoice-info-card';
import { InvoiceCommissionSection } from './invoice-commission-section';
import { InvoiceItemsTab } from './invoice-items-tab';
import { InvoiceTransactionsTab } from './invoice-transactions-tab';
import { InvoiceLogsTab } from './invoice-logs-tab';
import { MailHistoryTab } from './mail-history-tab';
import { InvoiceSignedDocumentTab } from './invoice-signed-document-tab';

const TABS = [
  { value: 'items', label: 'Items' },
  { value: 'transactions', label: 'Transactions' },
  { value: 'logs', label: 'Logs' },
  { value: 'mail-history', label: 'Mail History' },
  { value: 'payment-request', label: 'Đề nghị thanh toán' },
  { value: 'reconciliation-statement', label: 'Biên bản đối soát' },
] as const;

const TAB_VALUES = TABS.map((t) => t.value) as readonly string[];

/**
 * Invoice detail page — identity header (code + status/type pills + status-gated
 * actions) over a totals band and the info card, then an Items/Transactions/Logs
 * tab switcher persisted in `?tab=` (lazy — each tab mounts only when active).
 * Mirrors the contract detail shell (fullwidth, no read-drawer).
 */
export default function InvoiceDetailPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam) || 0;
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab');
  const activeTab = requestedTab && TAB_VALUES.includes(requestedTab) ? requestedTab : 'items';

  const { data, isLoading, isError, refetch } = useInvoiceDetail(id);
  const detail = data?.result;

  const onTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container size="full">
      {isError ? (
        <TabError title="Failed to load invoice" onRetry={() => refetch()} />
      ) : isLoading || !detail ? (
        <TabLoading rows={4} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <InvoiceDetailHeader id={id} detail={detail} />
          <InvoiceTotalsCard id={id} detail={detail} />
          <InvoiceInfoCard detail={detail} />
          <InvoiceCommissionSection detail={detail} />

          <Tabs value={activeTab} onValueChange={onTabChange}>
            <TabsList>
              {TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="items">
              {activeTab === 'items' ? <InvoiceItemsTab id={id} currency={detail.invoice_currency} /> : null}
            </TabsContent>
            <TabsContent value="transactions">
              {activeTab === 'transactions' ? <InvoiceTransactionsTab id={id} currency={detail.invoice_currency} /> : null}
            </TabsContent>
            <TabsContent value="logs">
              {activeTab === 'logs' ? <InvoiceLogsTab id={id} /> : null}
            </TabsContent>
            <TabsContent value="mail-history">
              {activeTab === 'mail-history' ? <MailHistoryTab id={id} /> : null}
            </TabsContent>
            <TabsContent value="payment-request">
              {activeTab === 'payment-request' ? (
                <InvoiceSignedDocumentTab invoiceId={id} docType="payment-request" />
              ) : null}
            </TabsContent>
            <TabsContent value="reconciliation-statement">
              {activeTab === 'reconciliation-statement' ? (
                <InvoiceSignedDocumentTab invoiceId={id} docType="reconciliation-statement" />
              ) : null}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </Container>
  );
}
